import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export type RefreshHandler = () => Promise<unknown> | void;

interface ListViewContextValue {
  registerRefresh(refreshHandler: RefreshHandler): void;
  unregisterRefresh(): void;
  refresh(): Promise<void>;
  refreshing: boolean;
  hasRefreshHandler: boolean;
  tableFilter?: Record<string, any>;
  setTableFilter(filter: Record<string, any> | undefined): void;
}

const defaultContext: ListViewContextValue = {
  registerRefresh: () => undefined,
  unregisterRefresh: () => undefined,
  refresh: () => Promise.resolve(),
  refreshing: false,
  hasRefreshHandler: false,
  setTableFilter: () => undefined,
};

const ListViewContext = createContext<ListViewContextValue>(defaultContext);

const NestedListContext = createContext(false);

export function NestedListScope({ children }: PropsWithChildren<{}>) {
  return (
    <NestedListContext.Provider value>{children}</NestedListContext.Provider>
  );
}

export function ListViewProvider({ children }: PropsWithChildren<{}>) {
  const refreshHandlerRef = useRef<RefreshHandler | undefined>();
  const [refreshing, setRefreshing] = useState(false);
  const [hasRefreshHandler, setHasRefreshHandler] = useState(false);
  const [tableFilter, setTableFilter] = useState<Record<string, any>>();

  const registerRefresh = useCallback((refreshHandler: RefreshHandler) => {
    refreshHandlerRef.current = refreshHandler;
    setHasRefreshHandler(true);
  }, []);

  const unregisterRefresh = useCallback(() => {
    refreshHandlerRef.current = undefined;
    setHasRefreshHandler(false);
    setRefreshing(false);
  }, []);

  const refresh = useCallback(async () => {
    if (!refreshHandlerRef.current) return;
    setRefreshing(true);
    try {
      await refreshHandlerRef.current();
    } finally {
      setRefreshing(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      registerRefresh,
      unregisterRefresh,
      refresh,
      refreshing,
      hasRefreshHandler,
      tableFilter,
      setTableFilter,
    }),
    [
      registerRefresh,
      unregisterRefresh,
      refresh,
      refreshing,
      hasRefreshHandler,
      tableFilter,
    ]
  );

  return (
    <ListViewContext.Provider value={value}>
      {children}
    </ListViewContext.Provider>
  );
}

export function useListRefresh() {
  return useContext(ListViewContext);
}

export function useListRefreshBridge(refreshHandler?: RefreshHandler) {
  const { registerRefresh, unregisterRefresh } = useContext(ListViewContext);
  const nested = useContext(NestedListContext);

  useEffect(() => {
    if (nested || !refreshHandler) return;
    registerRefresh(refreshHandler);
    return () => unregisterRefresh();
  }, [refreshHandler, nested, registerRefresh, unregisterRefresh]);
}

export function useListTableFilter() {
  return useContext(ListViewContext).tableFilter;
}

export function useListTableFilterBridge(filter: Record<string, any>) {
  const { setTableFilter } = useContext(ListViewContext);
  const nested = useContext(NestedListContext);
  const serialized = JSON.stringify(filter);

  useEffect(() => {
    if (!nested) setTableFilter(JSON.parse(serialized));
  }, [nested, serialized, setTableFilter]);

  useEffect(() => {
    if (!nested) return () => setTableFilter(undefined);
  }, [nested, setTableFilter]);
}
